import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventModel, EventResponse } from '../types/Event';
import { formatDate } from '../utils/dateUtils';
import EventImage from './EventImage';
import RSVPButton from './RSVPButton';

interface EventHeaderProps {
  event: EventModel;
  onRSVP?: (response: EventResponse, email: string) => void;
  eventId?: string;
}

const EventHeader: React.FC<EventHeaderProps> = ({ event, onRSVP, eventId }) => {
  const { t } = useTranslation();
  const [showStoreModal, setShowStoreModal] = useState(false);

  const getStatusColor = () => {
    switch (event.status) {
      case 'active':
        return '#4CAF50';
      case 'canceled':
        return '#F44336';
      case 'past':
        return '#9E9E9E';
      default:
        return '#4CAF50';
    }
  };

  const getStatusText = () => {
    switch (event.status) {
      case 'active':
        return t('event.status.active');
      case 'canceled':
        return t('event.status.canceled');
      case 'past':
        return t('event.status.past');
      default:
        return t('event.status.active');
    }
  };

  const getResponseCounts = () => {
    const responses = event.eventResponses;
    return {
      yes: Object.values(responses).filter(r => r === 'yes').length,
      maybe: Object.values(responses).filter(r => r === 'maybe').length,
      no: Object.values(responses).filter(r => r === 'no').length,
    };
  };

  const counts = getResponseCounts();
  const totalResponses = counts.yes + counts.maybe + counts.no;
  const isDemoEvent = !eventId || eventId === 'demo';
  const isPrivateEvent = event.isOpenEvent === false;

  const handleRSVP = (response: EventResponse, email: string) => {
    if (onRSVP) {
      onRSVP(response, email);
    }
  };

  // If this is a private event, show only the private event message
  if (isPrivateEvent) {
    return (
      <div style={{
        padding: '40px 20px',
        color: 'white',
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Private Event Card */}
        <div style={{
          width: '100%',
          maxWidth: '480px',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          borderRadius: '32px',
          padding: '60px 40px',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}>
          {/* Subtle gradient overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            borderRadius: '32px',
            pointerEvents: 'none',
          }} />

          {/* Lock Icon */}
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
            position: 'relative',
            zIndex: 1,
          }}>
            🔒
          </div>

          {/* Private Event Title */}
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            margin: '0 0 16px 0',
            lineHeight: '1.2',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            position: 'relative',
            zIndex: 1,
          }}>
            {t('event.private.title')}
          </h1>

          {/* Private Event Message */}
          <p style={{
            fontSize: '15px',
            lineHeight: '1.6',
            opacity: 0.9,
            margin: '0 0 24px 0',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: '400',
            position: 'relative',
            zIndex: 1,
          }}>
            {t('event.private.message')}
          </p>

          {/* Call to Action */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: 1,
          }}>
            <p style={{
              color: 'white',
              fontSize: '14px',
              margin: '0 0 16px 0',
              lineHeight: '1.6',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
            }}>
              📱 <strong style={{ fontWeight: '600' }}>{t('event.private.cta')}</strong><br />
              {t('event.private.ctaText')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => {
                  // Try to open the app with custom scheme
                  const appUrl = `allezhopp://event/${eventId}`;
                  
                  // Create a hidden iframe to attempt the app launch
                  const iframe = document.createElement('iframe');
                  iframe.style.display = 'none';
                  iframe.src = appUrl;
                  document.body.appendChild(iframe);
                  
                  // Remove the iframe after a short delay
                  setTimeout(() => {
                    if (document.body.contains(iframe)) {
                      document.body.removeChild(iframe);
                    }
                  }, 1000);
                  
                  // Show a message after attempting to open
                  setTimeout(() => {
                    alert(t('event.private.appNotInstalled'));
                  }, 1500);
                }}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#667eea',
                  border: '1px solid #5a6fd8',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6fd8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                }}
              >
                {t('event.private.openInApp')}
              </button>
              
              <button
                onClick={() => {
                  window.open(
                    'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                    '_blank'
                  );
                }}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#4CAF50',
                  border: '1px solid #45a049',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#45a049';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4CAF50';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(76, 175, 80, 0.3)';
                }}
              >
                {t('event.private.downloadApp')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '40px 20px',
      color: 'white',
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Event Card */}
      <div style={{
        width: '100%',
        maxWidth: '480px',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderRadius: '32px',
        padding: '40px 32px',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle gradient overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          borderRadius: '32px',
          pointerEvents: 'none',
        }} />

        {/* Event Image and Status */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '32px',
          position: 'relative',
          zIndex: 1,
        }}>
          <EventImage 
            event={event} 
            size={120} 
            isCircular={true}
            showBorder={true}
          />
          
          {/* Event Status Badge */}
          <div style={{
            display: 'inline-block',
            backgroundColor: getStatusColor(),
            color: 'white',
            padding: '8px 20px',
            borderRadius: '24px',
            fontSize: '13px',
            fontWeight: '600',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            {getStatusText()}
          </div>
        </div>

        {/* Event Title */}
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          margin: '0 0 24px 0',
          lineHeight: '1.1',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}>
          {event.name}
        </h1>

        {/* Event Details Grid */}
        <div style={{
          display: 'grid',
          gap: '20px',
          marginBottom: '28px',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Date */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '18px 20px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            fontSize: '17px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}>
            <span style={{ fontSize: '24px', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>📅</span>
            <span style={{ fontWeight: '600', minWidth: '60px' }}>{t('event.details.date')}</span>
            <span style={{ fontWeight: '400', opacity: 0.95 }}>{event.eventDate ? formatDate(event.eventDate) : t('event.details.tba')}</span>
          </div>

          {/* Location */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '18px 20px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            fontSize: '17px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}>
            <span style={{ fontSize: '24px', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>📍</span>
            <span style={{ fontWeight: '600', minWidth: '80px' }}>{t('event.details.location')}</span>
            {event.location?.address ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'white',
                  textDecoration: 'none',
                  fontWeight: '400',
                  opacity: 0.95,
                  transition: 'all 0.3s ease',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                  paddingBottom: '2px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.95';
                  e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.3)';
                }}
              >
                {event.location.address}
              </a>
            ) : (
              <span style={{ fontWeight: '400', opacity: 0.95 }}>{t('event.details.tba')}</span>
            )}
          </div>

        </div>

        {/* Description */}
        {event.description && (
          <div style={{
            fontSize: '16px',
            lineHeight: '1.7',
            opacity: 0.9,
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            padding: '24px',
            borderRadius: '16px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: '400',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: 1,
            marginBottom: '28px',
          }}>
            "{event.description}"
          </div>
        )}

        {/* Response Section */}
        <div style={{
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Response Title */}
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            margin: '0 0 20px 0',
            textAlign: 'center',
            color: 'white',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          }}>
            {isDemoEvent ? t('event.responses.demoTitle') : t('event.responses.title')}
          </h3>

          {/* RSVP Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '20px',
          }}>
            <RSVPButton
              response="yes"
              isSelected={false}
              count={counts.yes}
              onRSVP={handleRSVP}
              eventId={eventId}
            />
            <RSVPButton
              response="maybe"
              isSelected={false}
              count={counts.maybe}
              onRSVP={handleRSVP}
              eventId={eventId}
            />
            <RSVPButton
              response="no"
              isSelected={false}
              count={counts.no}
              onRSVP={handleRSVP}
              eventId={eventId}
            />
          </div>

          {/* Response Count */}
          {totalResponses > 0 && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px',
              textAlign: 'center',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
              marginBottom: '24px',
            }}>
              {totalResponses} {totalResponses !== 1 ? t('event.responses.countPlural') : t('event.responses.count')} {t('event.responses.received')}
            </div>
          )}

          {/* Call to Action */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
          }}>
            <p style={{
              color: 'white',
              fontSize: '15px',
              margin: '0 0 16px 0',
              lineHeight: '1.6',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
            }}>
              {isDemoEvent ? (
                <>
                  🎭 <strong style={{ fontWeight: '600' }}>This is a demo event!</strong><br />
                  {t('event.responses.demoMessage')}
                </>
              ) : (
                <>
                  📱 <strong style={{ fontWeight: '600' }}>Want to participate?</strong><br />
                  {t('event.responses.realMessage')}
                </>
              )}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => {
                  // Try to open the app with custom scheme
                  const appUrl = `allezhopp://event/${eventId}`;
                  
                  // Create a hidden iframe to attempt the app launch
                  const iframe = document.createElement('iframe');
                  iframe.style.display = 'none';
                  iframe.src = appUrl;
                  document.body.appendChild(iframe);
                  
                  // Remove the iframe after a short delay
                  setTimeout(() => {
                    if (document.body.contains(iframe)) {
                      document.body.removeChild(iframe);
                    }
                  }, 1000);
                  
                  // Show a message after attempting to open
                  setTimeout(() => {
                    alert(t('event.private.appNotInstalled'));
                  }, 1500);
                }}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#667eea',
                  border: '1px solid #5a6fd8',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6fd8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                }}
              >
                {t('event.private.openInApp')}
              </button>
              
              <button
                onClick={() => setShowStoreModal(true)}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#4CAF50',
                  border: '1px solid #45a049',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#45a049';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4CAF50';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(76, 175, 80, 0.3)';
                }}
              >
                {isDemoEvent ? t('event.responses.downloadForReal') : t('event.responses.downloadToParticipate')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Store Selection Modal */}
      {showStoreModal && (
        <div
          onClick={() => setShowStoreModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#1A1A2E',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.title')}
            </h2>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.description')}
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}>
              <button
                onClick={() => {
                  window.open(
                    'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 9.11 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                </svg>
                {t('event.modal.appStore')}
              </button>
              <button
                onClick={() => {
                  window.open(
                    'https://play.google.com/store/apps/details?id=app.allezhopp.events&pcampaignid=web_share',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="currentColor"/>
                </svg>
                {t('event.modal.googlePlay')}
              </button>
            </div>
            <button
              onClick={() => setShowStoreModal(false)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                color: '#666',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                marginTop: '0',
                width: 'fit-content',
                alignSelf: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('event.modal.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventHeader;
